import { CDK_DROP_LIST, DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import {
  FwDialogService,
  FwMenuTriggerDirective,
  FwSelectMenuComponent,
  FwTextInputComponent,
  FwTooltipDirective,
} from '@flywheel-io/vision';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import sortBy from 'lodash/sortBy';
import { forkJoin } from 'rxjs';
import { catchError, filter, first, switchMap, take } from 'rxjs/operators';

import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { PipesModule } from 'app/src/shared/pipes.module';
import { SharedModule } from 'app/src/shared/shared.module';

import { acquisitionLabelMaxLength, labelMinLength } from '../../app.constants';
import { ConfigService } from '../../config.service';
import { ContainerAttachmentsService } from '../../containers/attachments/container-attachments.service';
import { FileDownloadDialog } from '../../containers/file-download-dialog/file-download-dialog.component';
import { CoreApiService } from '../../core-api.service';
import { DialogService } from '../../dialogs';
import { DeleteDialogComponent, DeleteDialogResult } from '../../dialogs/delete-dialog/delete-dialog.component';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProjectsQueryService } from '../../projects/projects-query.service';
import { getContainerProjectId, pluralize } from '../../shared/container.utils';
import { ContainerType } from '../../shared/enums/container-type.enum';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { ReaderTaskContainerType } from '../../shared/enums/task.enum';
import { OriginInfo } from '../../shared/file.utils';
import { prohibitValues, validFileName } from '../../shared/form.utils';
import { Acquisition } from '../../shared/models/acquisition.model';
import { CVATSyncState, File, FileMoveBody } from '../../shared/models/file.model';
import { Option } from '../../shared/models/option.model';
import { Session } from '../../shared/models/session.model';
import { ViewerApp } from '../../shared/models/site-settings.model';
import { firstValueFrom, tryOperations } from '../../shared/observable.utils';
import { CreateReaderTaskDialogComponent } from '../../task-manager/create-reader-task-dialog/create-reader-task-dialog.component';
import { AcquisitionUploadDialog } from '../../upload/acquisition-upload/acquisition-upload-dialog.component';
import { ViewerParameters } from '../../viewers/frame/viewer-frame.model';
import { FileType } from '../../viewers/types';
import { ViewerService } from '../../viewers/viewer.service';
import {
  AcquisitionInfoDialog,
  AcquisitionInfoDialogResult,
} from '../acquisition-info-dialog/acquisition-info-dialog.component';
import { AcquisitionNotesDialog } from '../acquisition-notes-dialog/acquisition-notes-dialog.component';
import { AnalysesDeleteConflictDialogComponent } from '../analyses-delete-conflict-dialog/analyses-delete-conflict-dialog.component';
import { SessionService } from '../session.service';
import { SessionDataState } from '../session-data/session-data.state';

@Component({
  selector: 'acquisition-files',
  templateUrl: './acquisition-files.component.html',
  styleUrls: ['./acquisition-files.component.scss'],
  imports: [
    CommonModule,
    ContainerModule,
    DirectivesModule,
    DragDropModule,
    FileModule,
    FormsModule,
    FwMenuTriggerDirective,
    FwSelectMenuComponent,
    FwTextInputComponent,
    MaterialModule,
    PipesModule,
    ReactiveFormsModule,
    RouterModule,
    SharedModule,
    FwTooltipDirective,
  ],
})
export class AcquisitionFilesComponent implements AfterViewInit {
  private coreApi = inject(CoreApiService);
  private logger = inject(LoggingService);
  private notifications = inject(NotificationService);
  private sessionService = inject(SessionService);
  private viewerService = inject(ViewerService);
  private projectQueryService = inject(ProjectsQueryService);
  state = inject(SessionDataState);
  private configService = inject(ConfigService);
  private legacyDialog = inject(DialogService);
  private dialog = inject(FwDialogService);
  private containerAttachments = inject(ContainerAttachmentsService);
  private dropList = inject(CDK_DROP_LIST, { optional: true });
  private destroyRef = inject(DestroyRef);

  fileTypeContainer = viewChildren<ElementRef>('fileTypeContainer');
  fileActionsElement = viewChildren<ElementRef>('fileActions');

  acquisition = input<Acquisition>();
  checked = input<boolean>();
  files = input<File[]>(); // filtered/sorted files
  session = input<Session>();

  checkedChange = output<boolean>();
  stagedChanged = output<string | null>(); // acquisition._id for save, or null for cancel

  showHoverIcons = false;

  protected hasDicomFiles = computed(() => this.files()?.some((file) => file.type === FileType.dicom));

  acquisitionFiles = signal<File[]>([]);
  disableFileDrag = signal(false);
  readonly features = {
    cvat: this.configService.isFeatureEnabled('cvat'),
    tasksRefactor: this.configService.isFeatureEnabled('tasks_refactor'),
    ohifV3DefaultApp: this.configService.isFeatureEnabled('ohif_v3_default_app'),
    ohifV3: this.configService.isFeatureEnabled('ohif_v3'),
    segmentationsV3: this.configService.isFeatureEnabled('segmentations_v3'),
  };
  fileRenames = signal<Record<string, FormControl>>({});
  fileTypes = signal<Option[]>([]);
  fileViewParameters = computed<ViewerParameters>(() => ({
    fileIds: this.acquisitionFiles().map((file) => file._id),
  }));
  permissions = signal({
    deleteContainer: false,
    downloadFiles: false,
    modifyFileMetadata: false,
    moveFiles: false,
    renameFiles: false,
    tasksModify: false,
    uploadFiles: false,
    viewMetadata: true,
    viewNotes: false,
  });
  stagingForm = new FormGroup({
    label: new FormControl('', [
      Validators.required,
      Validators.minLength(labelMinLength),
      Validators.maxLength(acquisitionLabelMaxLength),
    ]),
  });
  typeChanges = signal<Record<string, FormControl>>({});
  viewerApps = signal<ViewerApp[]>([]);

  projectId = computed(() => (this.acquisition() ? getContainerProjectId(this.acquisition()) : undefined));
  projectQuery = this.projectQueryService.injectProjectQuery(this.projectId);
  isProjectLocked = computed(() => Boolean(this.projectQuery.data().locked));

  CVATSyncStateTooltipMap = {
    [CVATSyncState.Synced]: 'File synced - Ready for Annotation',
    [CVATSyncState.NotSynced]: 'File is syncing - Not ready for Annotation',
    [CVATSyncState.Error]: 'Sync Error - Annotation disabled',
  };

  CVATSyncStateClassMap = {
    [CVATSyncState.Synced]: 'synced-icon',
    [CVATSyncState.NotSynced]: 'not-synced-icon',
    [CVATSyncState.Error]: 'error-icon',
  };

  CVATSyncStateIconMap = {
    [CVATSyncState.Synced]: 'check_circle',
    [CVATSyncState.NotSynced]: 'cached',
    [CVATSyncState.Error]: 'error',
  };

  constructor() {
    if (!this.dropList) {
      // only allow dragging if we're within a drop list
      this.disableFileDrag.set(true);
    }

    this.state.projectPermissions$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((permissions) => {
      this.permissions.set({
        deleteContainer: permissions.has(PermissionAction.ContainersDeleteHierarchy),
        downloadFiles: permissions.has(PermissionAction.FilesDownload),
        modifyFileMetadata: permissions.has(PermissionAction.FilesModifyMetadata),
        moveFiles: permissions.has(PermissionAction.FilesMove),
        renameFiles: permissions.has(PermissionAction.FilesMove),
        tasksModify: permissions.has(PermissionAction.TasksModify),
        uploadFiles: permissions.has(PermissionAction.FilesCreateSingle),
        viewMetadata: permissions.has(PermissionAction.ContainersViewMetadata),
        viewNotes: permissions.has(PermissionAction.NotesView),
      });
    });

    this.configService
      .getFileTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((fileTypes) => {
        const displayNames = sortBy(Object.keys(fileTypes), (fileType) => fileType.toLowerCase());
        this.fileTypes.set(displayNames.map((fileType) => ({ value: fileType, title: fileType })));
      });
    this.state.fileRename$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((fileId) => {
      const file = (this.acquisition()?.files ?? []).find((f) => f.file_id === fileId);
      if (file) {
        this.startFileRename(file);
      }
    });

    const acquisitionId = computed(() => this.acquisition()?._id);
    effect(() => {
      const id = acquisitionId();
      if (!id) {
        return;
      }
      const acquisition = untracked(() => this.acquisition());
      const projectId = getContainerProjectId(acquisition);
      this.viewerService
        .getAssociatedApps({ container: acquisition, projectId })
        .pipe(first(), takeUntilDestroyed(this.destroyRef))
        .subscribe((apps) => this.viewerApps.set(apps));
    });

    effect(() => {
      this.stagingForm.get('label').reset(this.acquisition()?.label);
    });

    effect(() => {
      this.acquisitionFiles.set(this.files() ?? this.acquisition()?.files ?? []);
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.shouldShowHoverIcons();
    }, 0);
  }

  get isStagedAcquisition(): boolean {
    return !this.acquisition()._id;
  }

  get hasPendingRenames(): boolean {
    return Object.keys(this.fileRenames()).length > 0;
  }

  get timestamp(): string {
    const timestamp = this.acquisition()?.timestamp;
    if (isEmpty(timestamp)) {
      return '';
    }
    const timezone = this.acquisition().timezone || this.session()?.timezone;
    const fullFormatted = this.sessionService.formattedTimestamp({ timestamp, timezone });
    if (this.sessionService.showTimezone$.value) {
      return fullFormatted;
    }
    let components = fullFormatted.split(' ');
    if (this.session()?.timezone === timezone && components.length === 3) {
      // timezone is already omitted if showTimezone$ is false, but just in case
      components = components.slice(0, -1); // omit timezone
    }
    const sessionDate = (this.session()?.timestamp ?? '').split('T')[0];
    const acquisitionDate = timestamp.split('T')[0];
    if (sessionDate === acquisitionDate) {
      components = components.slice(1); // omit date
    }
    return components.join(' ');
  }

  getFileClassification(file: File): string {
    return this.sessionService.getFileClassification(file);
  }

  getFileOrigin(file: File): OriginInfo {
    return this.sessionService.getFileOrigin(file, this.acquisition()['join-origin']);
  }

  isQuarantined(file: File): boolean {
    return file.virus_scan?.state === 'quarantined';
  }

  async selectFile(event: MouseEvent, file: File): Promise<void> {
    const selectedFileIds = await firstValueFrom(this.state.fileSelection$);
    if (event.shiftKey) {
      if (selectedFileIds.has(file.file_id)) {
        selectedFileIds.delete(file.file_id);
      } else {
        selectedFileIds.add(file.file_id);
      }
    } else {
      selectedFileIds.clear();
    }
    this.state.fileSelection$.next(selectedFileIds);
  }

  information(): void {
    this.legacyDialog
      .open(AcquisitionInfoDialog, {
        data: {
          acquisition: this.acquisition(),
        },
      })
      .afterClosed()
      .pipe(filter((result: AcquisitionInfoDialogResult) => Boolean(result?.dirty)))
      .subscribe(() => {
        this.sessionService.acquisitionChange$.next(null);
      });
  }

  downloadFiles(): void {
    const nodes = [{ level: 'acquisition', _id: this.acquisition()._id }];
    this.legacyDialog.open(FileDownloadDialog, {
      data: { requestBody: { nodes } },
    });
  }

  delete(): void {
    let deleteReason: string | undefined;
    this.dialog
      .openDialog(DeleteDialogComponent, {
        data: {
          content: `Are you sure you want to delete the acquisition "${this.acquisition().label || this.acquisition()._id}"?`,
        },
      })
      .closed.pipe(
        filter((result: DeleteDialogResult) => Boolean(result?.delete)),
        switchMap((result) => {
          deleteReason = result.deleteReason;
          return this.coreApi.containers.delete<Acquisition>(
            this.acquisition()._id,
            result.deleteReason ? { delete_reason: result.deleteReason } : {},
          );
        }),
      )
      .subscribe({
        next: () => {
          this.notifications.success('acquisition.delete.success');
          this.sessionService.acquisitionChange$.next(null);
        },
        error: (error: HttpErrorResponse) => {
          const messages = {
            error: 'acquisition.delete.error', // generic error
            analysis_conflict: 'acquisition.delete.analysisConflict',
            original_data_present: 'acquisition.delete.originalData',
          };
          const reason = get(error, 'error.reason');
          if (error.error.message.startsWith('Analysis conflict:')) {
            const ids = String(error.error.message).match(/[0-9a-f]{24}/g);
            if (ids?.length) {
              this.deleteConflictDialog(deleteReason, ids);
            }
          } else if (reason in messages) {
            this.notifications.error(messages[reason]);
          } else {
            this.notifications.error(messages['error']);
          }
        },
      });
  }

  async deleteConflictDialog(reason: string, analyses: string[]): Promise<void> {
    const analysesList = await firstValueFrom(forkJoin(analyses.map((id) => this.coreApi.analyses.get(id))));
    const ref = this.dialog.openDialog(AnalysesDeleteConflictDialogComponent, {
      data: { conflicts: analysesList, source: 'acquisition' },
    });
    ref.closed
      .pipe(
        filter((result: DeleteDialogResult) => Boolean(result?.delete)),
        switchMap(() => {
          return forkJoin(
            analysesList.map((analysis) => {
              return this.coreApi.analyses
                .delete(
                  pluralize(analysis.parent.type),
                  analysis.parent.id,
                  analysis._id,
                  reason ? { delete_reason: reason } : {},
                )
                .pipe(
                  catchError((error) => {
                    this.notifications.error('Error deleting analyses', true);
                    throw error;
                  }),
                );
            }),
          ).pipe(
            switchMap(() => {
              return this.coreApi.acquisitions.delete(this.acquisition()._id, reason ? { delete_reason: reason } : {});
            }),
            catchError((error) => {
              this.notifications.error('Error deleting acquisition', true);
              throw error;
            }),
          );
        }),
      )
      .subscribe(() => {
        this.notifications.success('Acquisition and associated analyses deleted successfully', true);
        this.sessionService.acquisitionChange$.next(null);
      });
  }

  createFile(): void {
    this.containerAttachments.createFile(this.acquisition()).then(() => {
      this.sessionService.acquisitionChange$.next(null);
    });
  }

  uploadFiles(): void {
    this.legacyDialog
      .open(AcquisitionUploadDialog, {
        data: {
          session: this.session(),
          acquisition: this.acquisition(),
        },
      })
      .afterClosed()
      .pipe(filter((proceed) => Boolean(proceed)))
      .subscribe(() => {
        this.sessionService.acquisitionChange$.next(null);
      });
  }

  notes(): void {
    this.legacyDialog.open(AcquisitionNotesDialog, {
      data: {
        acquisition: this.acquisition(),
      },
    });
  }

  protected createReaderTask(): void {
    this.dialog
      .openDialog(CreateReaderTaskDialogComponent, {
        closeOnDestroy: true,
        disableClose: true,
        data: {
          projectId: getContainerProjectId(this.acquisition()),
          containerType: ReaderTaskContainerType.Acquisition,
          containerId: this.acquisition()._id,
        },
      })
      .closed.pipe(take(1))
      .subscribe();
  }

  viewAcquisition(app: ViewerApp): void {
    this.viewerService
      .view({
        app: app,
        container: this.acquisition(),
      })
      .subscribe((result) => {
        if (result?.dirty) {
          this.sessionService.acquisitionChange$.next(null);
        }
      });
  }

  startFileRename(file: File): void {
    if (!this.permissions().renameFiles) {
      return;
    }
    const otherFilenames = this.acquisition()
      .files.map((file) => file.name)
      .filter((name) => name !== file.name);
    this.fileRenames.set({
      ...this.fileRenames(),
      [file.file_id]: new FormControl(file.name, [Validators.required, validFileName, prohibitValues(otherFilenames)]),
    });
  }

  cancelFileRename(file: File): void {
    this.fileRenames.set(omit(this.fileRenames(), file.file_id));
    if (file.parents.acquisition !== this.acquisition()._id) {
      this.state.fileMove$.next([{ file, acquisitionId: file.parents.acquisition }]);
    }
  }

  saveFileRename(file: File): void {
    const control = this.fileRenames()[file.file_id];
    if (control.invalid) {
      return;
    }
    if (this.isStagedAcquisition) {
      this.fileRenames.set(omit(this.fileRenames(), file.file_id));
      file.name = control.value;
      return;
    }
    const renamePayload: FileMoveBody = {
      name: control.value,
      run_gear_rules: this.state.runGearRulesOnMove$.value ?? false,
    };
    if (this.acquisition()._id !== file.parents.acquisition) {
      renamePayload.container_reference = { type: ContainerType.Acquisition, id: this.acquisition()._id };
    }
    this.coreApi.files.move(file.file_id, renamePayload).subscribe({
      next: () => {
        this.fileRenames.set(omit(this.fileRenames(), file.file_id));
        file.name = control.value;
        file.parents.acquisition = this.acquisition()._id;
        this.notifications.success('Successfully renamed file.', true);
      },
      error: (error) => {
        this.logger.error(error);
        this.notifications.error('Unable to rename file.', true);
      },
    });
  }

  toggleTypeSelect(file: File): void {
    if (!this.permissions().modifyFileMetadata) {
      return;
    }
    if (file.file_id in this.typeChanges()) {
      this.typeChanges.set(omit(this.typeChanges(), file.file_id));
    } else {
      this.typeChanges.set({ ...this.typeChanges(), [file.file_id]: new FormControl(file.type) });
    }
  }

  saveFileTypeSelection(file: File): void {
    this.coreApi.files.put(file.file_id, { type: this.typeChanges()[file.file_id].value }).subscribe({
      next: () => {
        file.type = this.typeChanges()[file.file_id].value;
        this.toggleTypeSelect(file);
        this.notifications.success('File type changed successfully.', true);
      },
      error: (error) => {
        this.notifications.error('File type could not be changed.', true);
        this.logger.error(error);
      },
    });
  }

  getErrorMessage(fileId: string): string {
    const control = this.fileRenames()[fileId];
    if (control.hasError('required')) {
      return 'File name is required.';
    }
    if (control.hasError('pattern')) {
      return 'Invalid characters';
    }
    if (control.hasError('prohibitValues')) {
      return 'There is a file with this name already in the acquisition.';
    }
    return '';
  }

  async saveStagedAcquisition(): Promise<void> {
    if (!this.stagingForm.valid || this.hasPendingRenames) {
      return;
    }

    let acquisitionId: string;
    try {
      const acquisition = await firstValueFrom(
        this.coreApi.acquisitions.post({
          label: this.stagingForm.get('label').value,
          session: this.session()._id,
        }),
      );
      acquisitionId = acquisition._id;
    } catch (error) {
      if (error.status === 409) {
        this.notifications.error('acquisition.create.duplicate');
      } else {
        this.logger.error(error);
        this.notifications.error('acquisition.create.error');
      }
      return;
    }

    const moveOutcomes = await firstValueFrom(
      tryOperations(this.acquisition().files, (file) =>
        this.coreApi.files.move(file.file_id, {
          container_reference: { type: ContainerType.Acquisition, id: acquisitionId },
          name: file.name,
          run_gear_rules: this.state.runGearRulesOnMove$.value ?? false,
        }),
      ),
    );
    const movedFiles = moveOutcomes.filter((outcome) => outcome.result).map((outcome) => outcome.source);
    for (const file of movedFiles) {
      file.parents.acquisition = acquisitionId;
    }
    const failedFiles = moveOutcomes.filter((outcome) => outcome.error).map((outcome) => outcome.source);
    if (failedFiles.length > 0) {
      this.logger.error(moveOutcomes.find((outcome) => outcome.error).error);
      this.notifications.error(movedFiles.length > 0 ? 'files.move.partialError' : 'files.move.error');
    } else {
      this.notifications.success('files.move.newAcquisition');
    }
    this.stagedChanged.emit(acquisitionId);
  }

  cancelStagedAcquisition(): void {
    this.stagedChanged.emit(null);
  }

  showCvatIcon(file: File): boolean {
    return this.features.cvat && Boolean(file.cvat);
  }

  getCvatTooltip(file: File): string {
    return file.cvat.error_summary || this.CVATSyncStateTooltipMap[file.cvat.sync_state];
  }

  @HostListener('window:resize')
  onResize(): void {
    this.showHoverIcons = false;
    setTimeout(() => {
      this.shouldShowHoverIcons();
    }, 0);
  }

  shouldShowHoverIcons(): void {
    const fileTypeContainerLocationArray = this.fileTypeContainer();
    const fileActionsLocationsArray = this.fileActionsElement();

    let show = true;

    for (let i = 0; i < fileTypeContainerLocationArray.length; i++) {
      const fileTypeContainerLocation = fileTypeContainerLocationArray[i]?.nativeElement?.getBoundingClientRect();
      const fileActionsLocations = fileActionsLocationsArray[i]?.nativeElement?.getBoundingClientRect();
      if (!fileTypeContainerLocation || !fileActionsLocations) {
        continue;
      }
      const horizontalDistance = Math.abs(fileActionsLocations.left - fileTypeContainerLocation.right);

      if (horizontalDistance < 110) {
        show = false;
        break;
      }
    }
    this.showHoverIcons = show;
  }
}
